/**
 * Mock 数据与 API 拦截器
 * 
 * 在后端 API 就绪前，使用 mock 数据驱动前端开发。
 * 通过 axios 拦截器拦截请求并返回模拟数据。
 * 
 * 切换真实 API：设置 VITE_USE_MOCK=false 或移除此文件导入
 */

import type { AxiosRequestConfig } from 'axios'
import type {
  User,
  Profile,
  TimelineEntry,
  Gallery,
  Media,
  Task,
  TaskRun,
  RunLog,
  WorkbenchSummary,
  PaginatedResponse,
  Attachment,
  ScheduleItem
} from '@/types'

// ========== Mock 数据 ==========

const mockUser: User = {
  id: 'user-001',
  username: 'admin',
  display_name: '管理员',
  avatar_url: undefined,
  created_at: '2024-01-01T00:00:00Z',
  updated_at: '2024-01-01T00:00:00Z'
}

/** 生成日期键：YYYY-MM-DD（本地时区） */
function mockDateKey(offsetDays: number): string {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** Mock 日程数据（围绕今天播种，便于热力图演示） */
const mockSchedules: ScheduleItem[] = [
  {
    id: 'sch-001',
    user_id: 'user-001',
    title: '组会汇报',
    schedule_date: mockDateKey(0),
    start_time: '09:30:00',
    end_time: '11:00:00',
    note: '同步本周进展',
    source: 'manual',
    external_id: null,
    deleted_at: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 'sch-002',
    user_id: 'user-001',
    title: '代码评审',
    schedule_date: mockDateKey(0),
    start_time: '15:00:00',
    end_time: null,
    note: null,
    source: 'manual',
    external_id: null,
    deleted_at: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 'sch-003',
    user_id: 'user-001',
    title: '季度目标对齐',
    schedule_date: mockDateKey(-2),
    start_time: null,
    end_time: null,
    note: '全天',
    source: 'manual',
    external_id: null,
    deleted_at: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 'sch-004',
    user_id: 'user-001',
    title: '技术分享',
    schedule_date: mockDateKey(2),
    start_time: '19:00:00',
    end_time: '20:30:00',
    note: null,
    source: 'manual',
    external_id: null,
    deleted_at: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  }
]

const mockProfile: Profile = {
  id: 'profile-001',
  user_id: 'user-001',
  name: '张三',
  birth_date: '1995-03-15',
  joined_party_at: '2018-06-01',
  joined_league_at: '2010-05-04',
  contact_json: { location: '北京市海淀区', website: 'https://example.com' },
  markdown_body: '全栈开发工程师，热爱开源技术。专注于 Python 和 Vue 生态。',
  age: 29,
  party_tenure_years: 6,
  league_tenure_years: 14,
  created_at: '2024-01-01T00:00:00Z',
  updated_at: '2024-06-15T08:30:00Z'
}

const mockTimelineEntries: TimelineEntry[] = [
  {
    id: 'tl-001',
    title: '清华大学计算机科学与技术学士',
    summary: 'GPA 3.8/4.0，获得国家奖学金',
    start_date: '2013-09-01',
    end_date: '2017-07-01',
    entry_type: 'education',
    sort_order: 1,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 'tl-002',
    title: '某科技有限公司 后端开发工程师',
    summary: '负责微服务架构设计与开发，主要使用 Python/FastAPI',
    start_date: '2017-07-15',
    end_date: '2020-03-31',
    entry_type: 'work',
    sort_order: 2,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 'tl-003',
    title: '某互联网公司 高级软件工程师',
    summary: '负责自动化运维平台开发，容器化部署方案落地',
    start_date: '2020-04-15',
    end_date: undefined,
    entry_type: 'work',
    sort_order: 3,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 'tl-004',
    title: '校级优秀毕业生',
    summary: '',
    start_date: '2017-06-20',
    end_date: undefined,
    entry_type: 'award',
    sort_order: 4,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 'tl-005',
    title: '某科技公司 暑期实习',
    summary: '参与推荐系统开发，负责特征工程模块',
    start_date: '2016-07-01',
    end_date: '2016-08-31',
    entry_type: 'internship',
    sort_order: 5,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 'tl-006',
    title: '分布式任务调度平台',
    summary: '基于 Python/FastAPI 与 Vue3 实现的任务调度与监控系统',
    start_date: '2022-03-01',
    end_date: '2022-09-30',
    entry_type: 'project',
    sort_order: 6,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 'tl-007',
    title: 'AWS Certified Solutions Architect',
    summary: 'AWS 解决方案架构师专业认证',
    start_date: '2023-05-15',
    end_date: undefined,
    entry_type: 'certification',
    sort_order: 7,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 'tl-008',
    title: '市级程序设计大赛一等奖',
    summary: '由市科协与高校联合主办，获得程序设计赛道一等奖',
    start_date: '2016-11-20',
    end_date: undefined,
    entry_type: 'award',
    sort_order: 8,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  }
]

const mockAttachments: Attachment[] = [
  {
    id: 'att-001',
    owner_id: 'user-001',
    scope_type: 'timeline_entry',
    scope_id: 'tl-001',
    original_name: '学位证书.pdf',
    storage_key: 'attachments/att-001.pdf',
    mime_type: 'application/pdf',
    size_bytes: 2048576,
    sha256: 'mock-sha256-001',
    visibility: 'private',
    created_at: '2024-01-15T10:00:00Z',
    updated_at: '2024-01-15T10:00:00Z'
  },
  {
    id: 'att-002',
    owner_id: 'user-001',
    scope_type: 'timeline_entry',
    scope_id: 'tl-001',
    original_name: '获奖证书.jpg',
    storage_key: 'attachments/att-002.jpg',
    mime_type: 'image/jpeg',
    size_bytes: 512000,
    sha256: 'mock-sha256-002',
    visibility: 'private',
    created_at: '2024-02-20T14:30:00Z',
    updated_at: '2024-02-20T14:30:00Z'
  }
]

const mockGalleries: Gallery[] = [
  {
    id: 'gal-001',
    name: '默认图库',
    is_public: false,
    is_default: true,
    media_count: 12,
    total_size: 52428800,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 'gal-002',
    name: '项目截图',
    is_public: true,
    is_default: false,
    media_count: 8,
    total_size: 31457280,
    created_at: '2024-03-01T00:00:00Z',
    updated_at: '2024-03-01T00:00:00Z'
  },
  {
    id: 'gal-003',
    name: '个人照片',
    is_public: false,
    is_default: false,
    media_count: 25,
    total_size: 104857600,
    created_at: '2024-04-01T00:00:00Z',
    updated_at: '2024-04-01T00:00:00Z'
  }
]

const mockMedia: Media[] = [
  {
    id: 'med-001',
    gallery_id: 'gal-001',
    file_id: 'file-001',
    filename: 'screenshot_001.png',
    file_size: 2048576,
    mime_type: 'image/png',
    width: 1920,
    height: 1080,
    is_public: false,
    created_at: '2024-05-01T10:00:00Z',
    updated_at: '2024-05-01T10:00:00Z'
  },
  {
    id: 'med-002',
    gallery_id: 'gal-001',
    file_id: 'file-002',
    filename: 'banner.jpg',
    file_size: 512000,
    mime_type: 'image/jpeg',
    width: 2560,
    height: 800,
    is_public: true,
    public_id: 'pub-abc123',
    created_at: '2024-05-02T14:30:00Z',
    updated_at: '2024-05-02T14:30:00Z'
  },
  {
    id: 'med-003',
    gallery_id: 'gal-002',
    file_id: 'file-003',
    filename: 'demo_video.mp4',
    file_size: 15728640,
    mime_type: 'video/mp4',
    width: 1920,
    height: 1080,
    duration: 120,
    is_public: false,
    expires_at: '2024-12-31T23:59:59Z',
    created_at: '2024-05-03T09:00:00Z',
    updated_at: '2024-05-03T09:00:00Z'
  }
]

const mockTasks: Task[] = [
  {
    id: 'task-001',
    name: '每日数据备份',
    description: '每天凌晨2点自动备份数据库到远程存储',
    entry_file: 'backup.py',
    cron_expression: '0 2 * * *',
    enabled: true,
    status: 'idle',
    resource_config: {
      cpu_limit: 0.5,
      memory_limit_mb: 256,
      timeout_seconds: 3600,
      log_max_size_mb: 50,
      allow_concurrent: false
    },
    mount_config: {
      mode: 'host_full',
      host_paths: ['/data', '/backup']
    },
    last_run_at: '2024-06-15T02:00:00Z',
    last_run_status: 'success',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-06-15T02:00:00Z'
  },
  {
    id: 'task-002',
    name: '网站健康检查',
    description: '每5分钟检查一次网站可用性',
    entry_file: 'health_check.py',
    cron_expression: '*/5 * * * *',
    enabled: true,
    status: 'running',
    resource_config: {
      cpu_limit: 0.25,
      memory_limit_mb: 128,
      timeout_seconds: 60,
      log_max_size_mb: 10,
      allow_concurrent: false
    },
    mount_config: {
      mode: 'task_dir'
    },
    last_run_at: '2024-06-15T10:00:00Z',
    last_run_status: 'success',
    created_at: '2024-02-01T00:00:00Z',
    updated_at: '2024-06-15T10:00:00Z'
  },
  {
    id: 'task-003',
    name: '日志清理',
    description: '清理30天前的应用日志',
    entry_file: 'cleanup.py',
    cron_expression: '0 3 * * 0',
    enabled: false,
    status: 'stopped',
    resource_config: {
      cpu_limit: 0.25,
      memory_limit_mb: 128,
      timeout_seconds: 600,
      log_max_size_mb: 20,
      allow_concurrent: false
    },
    mount_config: {
      mode: 'host_full',
      host_paths: ['/var/log']
    },
    created_at: '2024-03-01T00:00:00Z',
    updated_at: '2024-03-01T00:00:00Z'
  }
]

const mockTaskRuns: TaskRun[] = [
  {
    id: 'run-001',
    task_id: 'task-001',
    status: 'success',
    started_at: '2024-06-15T02:00:00Z',
    finished_at: '2024-06-15T02:05:23Z',
    exit_code: 0,
    container_id: 'container-abc123'
  },
  {
    id: 'run-002',
    task_id: 'task-001',
    status: 'failed',
    started_at: '2024-06-14T02:00:00Z',
    finished_at: '2024-06-14T02:01:45Z',
    exit_code: 1,
    error_message: 'Connection refused: remote storage unreachable',
    container_id: 'container-def456'
  },
  {
    id: 'run-003',
    task_id: 'task-002',
    status: 'success',
    started_at: '2024-06-15T10:00:00Z',
    finished_at: '2024-06-15T10:00:03Z',
    exit_code: 0,
    container_id: 'container-ghi789'
  }
]

const mockRunLogs: RunLog[] = [
  { id: 'log-001', run_id: 'run-001', timestamp: '2024-06-15T02:00:00Z', level: 'stdout', message: 'Starting backup process...' },
  { id: 'log-002', run_id: 'run-001', timestamp: '2024-06-15T02:00:05Z', level: 'stdout', message: 'Connecting to database...' },
  { id: 'log-003', run_id: 'run-001', timestamp: '2024-06-15T02:00:08Z', level: 'stdout', message: 'Database connection established' },
  { id: 'log-004', run_id: 'run-001', timestamp: '2024-06-15T02:02:00Z', level: 'stdout', message: 'Dumping tables...' },
  { id: 'log-005', run_id: 'run-001', timestamp: '2024-06-15T02:05:00Z', level: 'stdout', message: 'Uploading to remote storage...' },
  { id: 'log-006', run_id: 'run-001', timestamp: '2024-06-15T02:05:23Z', level: 'system', message: 'Backup completed successfully' }
]

const mockSummary: WorkbenchSummary = {
  modules: [
    { name: 'auth', label: '认证', status: 'active', icon: 'server' },
    { name: 'profile', label: '个人档案', status: 'active', icon: 'profile' },
    { name: 'gallery', label: '图床管理', status: 'active', icon: 'gallery' },
    { name: 'tasks', label: '自动化任务', status: 'active', icon: 'tasks' },
    { name: 'subsystems', label: '子系统管理', status: 'inactive', icon: 'subsystems' }
  ],
  storage: {
    quota_bytes: 5368709120,
    used_bytes: 2147483648,
    threshold_bytes: 4831838208,
    available_bytes: 288230376151711744
  },
  disk: {
    total_bytes: 107374182400,
    used_bytes: 42949672960,
    available_bytes: 64424509440,
    usage_percent: 40
  },
  memory: {
    total_bytes: 8589934592,
    used_bytes: 4294967296,
    available_bytes: 4294967296,
    usage_percent: 50
  },
  running_tasks: 1,
  enabled_tasks: 2,
  running_subsystems: 0,
  system_health: 'healthy'
}

// ========== Mock API 处理器 ==========

function delay(ms = 300): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export async function handleMockRequest(config: AxiosRequestConfig): Promise<unknown> {
  await delay(200 + Math.random() * 300)

  const url = config.url || ''
  const method = config.method?.toUpperCase() || 'GET'
  // FormData（文件上传）无法 JSON.parse，跳过
  const body = config.data && !(typeof FormData !== 'undefined' && config.data instanceof FormData)
    ? JSON.parse(config.data as string)
    : null

  // === 认证 ===
  if (url === '/auth/me' && method === 'GET') return mockUser
  if (url === '/auth/login' && method === 'POST') {
    return { user: mockUser, session_id: 'mock-session-001' }
  }
  if (url === '/auth/logout' && method === 'POST') return undefined
  if (url === '/auth/change-password' && method === 'POST') {
    // Mock 模式校验：当前密码必须非空、新密码至少 8 位（与后端 schema 一致）
    if (!body?.current_password) {
      throw { code: 'AUTH_INVALID_CREDENTIALS', message: '当前密码不正确', status: 400 }
    }
    if (!body?.new_password || body.new_password.length < 8) {
      throw { code: 'AUTH_WEAK_PASSWORD', message: '新密码至少需要 8 位', status: 422 }
    }
    return { message: 'Password changed successfully' }
  }

  // === 工作台摘要 ===
  if (url === '/workbench/summary' && method === 'GET') return mockSummary
  if (url === '/settings/storage' && method === 'GET') {
    return {
      quota_bytes: mockSummary.storage.quota_bytes,
      used_bytes: mockSummary.storage.used_bytes,
      threshold_bytes: mockSummary.storage.threshold_bytes
    }
  }
  if (url === '/settings/storage' && method === 'PATCH') {
    mockSummary.storage.quota_bytes = body.quota_bytes
    mockSummary.storage.threshold_bytes = Math.round(body.quota_bytes * 0.9)
    mockSummary.storage.available_bytes = Math.max(body.quota_bytes - mockSummary.storage.used_bytes, 0)
    return {
      quota_bytes: mockSummary.storage.quota_bytes,
      used_bytes: mockSummary.storage.used_bytes,
      threshold_bytes: mockSummary.storage.threshold_bytes
    }
  }
  if (url === '/settings/build-mirrors' && method === 'GET') {
    return {
      pip_index_url: 'https://mirrors.cloud.tencent.com/pypi/simple',
      pip_trusted_host: 'mirrors.cloud.tencent.com',
      npm_registry: 'https://mirrors.cloud.tencent.com/npm/',
      dockerhub_mirrors: ['https://mirror.ccs.tencentyun.com']
    }
  }
  if (url === '/settings/build-mirrors' && method === 'PATCH') {
    return {
      pip_index_url: body.pip_index_url || 'https://mirrors.cloud.tencent.com/pypi/simple',
      pip_trusted_host: body.pip_trusted_host || 'mirrors.cloud.tencent.com',
      npm_registry: body.npm_registry || 'https://mirrors.cloud.tencent.com/npm/',
      dockerhub_mirrors: ['https://mirror.ccs.tencentyun.com']
    }
  }
  if (url === '/health' && method === 'GET') {
    return {
      status: 'ok',
      version: '0.1.0',
      uptime_seconds: 86400,
      checks: { database: 'ok', storage: 'ok', worker: 'ok' }
    }
  }

  // === 个人档案 ===
  if (url === '/profile' && method === 'GET') return mockProfile
  if (url === '/profile' && method === 'PUT') {
    Object.assign(mockProfile, body, { updated_at: new Date().toISOString() })
    return mockProfile
  }

  // === 时间线 ===
  if (url === '/timeline-entries' && method === 'GET') {
    const page = parseInt(config.params?.page || '1')
    const pageSize = parseInt(config.params?.page_size || '20')
    const start = (page - 1) * pageSize
    const end = start + pageSize
    return {
      data: mockTimelineEntries.slice(start, end),
      meta: { page, page_size: pageSize, total: mockTimelineEntries.length, total_pages: Math.ceil(mockTimelineEntries.length / pageSize) }
    } as PaginatedResponse<TimelineEntry>
  }
  if (url.match(/\/timeline-entries\/.+\/attachments$/) && method === 'GET') {
    return {
      data: mockAttachments,
      meta: { page: 1, page_size: 20, total: mockAttachments.length, total_pages: 1 }
    } as PaginatedResponse<Attachment>
  }
  if (url.match(/\/timeline-entries\/.+/) && method === 'GET') {
    const id = url.split('/')[2]
    return mockTimelineEntries.find((e) => e.id === id) || null
  }
  if (url === '/timeline-entries' && method === 'POST') {
    const newEntry: TimelineEntry = {
      id: `tl-${Date.now()}`,
      ...body,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
    mockTimelineEntries.unshift(newEntry)
    return newEntry
  }
  if (url.match(/\/timeline-entries\/.+/) && method === 'PATCH') {
    const id = url.split('/')[2]
    const idx = mockTimelineEntries.findIndex((e) => e.id === id)
    if (idx >= 0) {
      Object.assign(mockTimelineEntries[idx], body, { updated_at: new Date().toISOString() })
      return mockTimelineEntries[idx]
    }
    return null
  }
  if (url.match(/\/timeline-entries\/.+/) && method === 'DELETE') {
    const id = url.split('/')[2]
    const idx = mockTimelineEntries.findIndex((e) => e.id === id)
    if (idx >= 0) mockTimelineEntries.splice(idx, 1)
    return undefined
  }

  // === 图库 ===
  if (url === '/galleries' && method === 'GET') return mockGalleries
  if (url === '/galleries' && method === 'POST') {
    const newGallery: Gallery = {
      id: `gal-${Date.now()}`,
      name: body.name,
      is_public: body.is_public || false,
      is_default: false,
      media_count: 0,
      total_size: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
    mockGalleries.push(newGallery)
    return newGallery
  }
  if (url.match(/\/galleries\/.+/) && method === 'PATCH') {
    const id = url.split('/')[2]
    const idx = mockGalleries.findIndex((g) => g.id === id)
    if (idx >= 0) {
      Object.assign(mockGalleries[idx], body, { updated_at: new Date().toISOString() })
      return mockGalleries[idx]
    }
    return null
  }
  if (url.match(/\/galleries\/.+/) && method === 'DELETE') {
    const id = url.split('/')[2]
    const idx = mockGalleries.findIndex((g) => g.id === id)
    if (idx >= 0 && !mockGalleries[idx].is_default) mockGalleries.splice(idx, 1)
    return undefined
  }

  // === 媒体 ===
  if (url.match(/\/galleries\/.+\/media/) && method === 'GET') {
    const galleryId = url.split('/')[2]
    const filtered = mockMedia.filter((m) => m.gallery_id === galleryId)
    const page = parseInt(config.params?.page || '1')
    const pageSize = parseInt(config.params?.page_size || '20')
    const start = (page - 1) * pageSize
    return {
      data: filtered.slice(start, start + pageSize),
      meta: { page, page_size: pageSize, total: filtered.length, total_pages: Math.ceil(filtered.length / pageSize) }
    } as PaginatedResponse<Media>
  }
  if (url.match(/\/media\/.+/) && method === 'PATCH') {
    const id = url.split('/')[2]
    const idx = mockMedia.findIndex((m) => m.id === id)
    if (idx >= 0) {
      Object.assign(mockMedia[idx], body, { updated_at: new Date().toISOString() })
      return mockMedia[idx]
    }
    return null
  }
  if (url.match(/\/media\/.+/) && method === 'DELETE') {
    const id = url.split('/')[2]
    const idx = mockMedia.findIndex((m) => m.id === id)
    if (idx >= 0) mockMedia.splice(idx, 1)
    return undefined
  }

  // === 任务文件上传（单份存储：直接落入任务目录）===
  if (url.match(/\/tasks\/.+\/files\/upload/) && method === 'POST') {
    const fd = config.data as FormData
    const files = fd ? (fd.getAll('files') as File[]) : []
    let rels: string[] = []
    try {
      rels = JSON.parse(String(fd.get('relative_paths') || '[]')) as string[]
    } catch {
      rels = []
    }
    // 与真实后端一致：优先使用相对路径（文件夹上传保留目录结构）
    const saved = files.map((f, i) => rels[i] || f.name)
    return { saved }
  }

  // === 任务 ===
  if (url === '/tasks' && method === 'GET') return mockTasks
  if (url === '/tasks' && method === 'POST') {
    const newTask: Task = {
      id: `task-${Date.now()}`,
      name: body.name,
      description: body.description || '',
      entry_file: body.entry_file || '',
      cron_expression: body.cron_expression || '',
      enabled: body.enabled !== false,
      status: 'idle',
      resource_config: {
        cpu_limit: 0.5,
        memory_limit_mb: 256,
        timeout_seconds: 3600,
        log_max_size_mb: 50,
        allow_concurrent: false,
        ...body.resource_config
      },
      mount_config: {
        mode: 'task_dir',
        ...body.mount_config
      },
      allow_public_egress: body.allow_public_egress || false,
      dns_servers: body.dns_servers || [],
      env_vars: body.env_vars || {},
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
    mockTasks.push(newTask)
    return newTask
  }
  if (url.match(/\/tasks\/.+/) && !url.includes('/files') && !url.includes('/runs') && method === 'GET') {
    const id = url.split('/')[2]
    return mockTasks.find((t) => t.id === id) || null
  }
  if (url.match(/\/tasks\/.+/) && !url.includes('/files') && !url.includes('/runs') && method === 'PATCH') {
    const id = url.split('/')[2]
    const idx = mockTasks.findIndex((t) => t.id === id)
    if (idx >= 0) {
      Object.assign(mockTasks[idx], body, { updated_at: new Date().toISOString() })
      return mockTasks[idx]
    }
    return null
  }
  if (url.match(/\/tasks\/.+/) && !url.includes('/files') && !url.includes('/runs') && method === 'DELETE') {
    const id = url.split('/')[2]
    const idx = mockTasks.findIndex((t) => t.id === id)
    if (idx >= 0) mockTasks.splice(idx, 1)
    return undefined
  }

  // === 任务启停 ===
  if (url.match(/\/tasks\/.+\/start/) && method === 'POST') {
    const id = url.split('/')[2]
    const task = mockTasks.find((t) => t.id === id)
    if (task) task.status = 'running'
    return { operation_id: `op-${Date.now()}`, status: 'accepted' }
  }
  if (url.match(/\/tasks\/.+\/stop/) && method === 'POST') {
    const id = url.split('/')[2]
    const task = mockTasks.find((t) => t.id === id)
    if (task) task.status = 'stopped'
    return { operation_id: `op-${Date.now()}`, status: 'accepted' }
  }
  if (url.match(/\/tasks\/.+\/restart/) && method === 'POST') {
    const id = url.split('/')[2]
    const task = mockTasks.find((t) => t.id === id)
    if (task) task.status = 'running'
    return { operation_id: `op-${Date.now()}`, status: 'accepted' }
  }
  if (url.match(/\/tasks\/.+\/install/) && method === 'POST') {
    return { operation_id: `op-${Date.now()}`, status: 'accepted' }
  }

  // === 任务运行记录 ===
  if (url.match(/\/tasks\/.+\/runs/) && !url.includes('/logs') && method === 'GET') {
    const taskId = url.split('/')[2]
    const filtered = mockTaskRuns.filter((r) => r.task_id === taskId)
    const page = parseInt(config.params?.page || '1')
    const pageSize = parseInt(config.params?.page_size || '20')
    const start = (page - 1) * pageSize
    return {
      data: filtered.slice(start, start + pageSize),
      meta: { page, page_size: pageSize, total: filtered.length, total_pages: Math.ceil(filtered.length / pageSize) }
    } as PaginatedResponse<TaskRun>
  }

  // === 任务日志 ===
  if (url.match(/\/tasks\/.+\/runs\/.+\/logs/) && method === 'GET') {
    const parts = url.split('/')
    const runId = parts[4]
    const filtered = mockRunLogs.filter((l) => l.run_id === runId)
    const page = parseInt(config.params?.page || '1')
    const pageSize = parseInt(config.params?.page_size || '100')
    const start = (page - 1) * pageSize
    return {
      data: filtered.slice(start, start + pageSize),
      meta: { page, page_size: pageSize, total: filtered.length, total_pages: Math.ceil(filtered.length / pageSize) }
    } as PaginatedResponse<RunLog>
  }

  // === 子系统 ===
  if (url === '/subsystems' && method === 'GET') return []
  if (url === '/subsystems' && method === 'POST') {
    return {
      id: `sub-${Date.now()}`,
      ...body,
      status: 'stopped',
      health_status: 'unknown',
      auto_restart: body.auto_restart || false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
  }

  // === 日程 ===
  if (url === '/schedules/month' && method === 'GET') {
    const year = Number(config.params?.year)
    const month = Number(config.params?.month)
    const prefix = `${year}-${String(month).padStart(2, '0')}-`
    const countMap = new Map<string, number>()
    let total = 0
    for (const s of mockSchedules) {
      if (s.deleted_at === null && s.schedule_date.startsWith(prefix)) {
        countMap.set(s.schedule_date, (countMap.get(s.schedule_date) || 0) + 1)
        total++
      }
    }
    return {
      year,
      month,
      total,
      days: Array.from(countMap.entries()).map(([date, count]) => ({ date, count }))
    }
  }
  if (url === '/schedules' && method === 'GET') {
    const date = String(config.params?.date || '')
    const items = mockSchedules
      .filter((s) => s.deleted_at === null && s.schedule_date === date)
      .sort((a, b) => (a.start_time || '99:99').localeCompare(b.start_time || '99:99'))
    return { date, total: items.length, items }
  }
  if (url === '/schedules' && method === 'POST') {
    const item: ScheduleItem = {
      id: `sch-${Date.now()}`,
      user_id: 'user-001',
      title: body.title,
      schedule_date: body.schedule_date,
      start_time: body.start_time ?? null,
      end_time: body.end_time ?? null,
      note: body.note ?? null,
      source: 'manual',
      external_id: null,
      deleted_at: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
    mockSchedules.push(item)
    return item
  }
  if (url.match(/\/schedules\/.+/) && method === 'PATCH') {
    const item = mockSchedules.find((s) => url.endsWith(`/${s.id}`))
    if (item) Object.assign(item, body)
    return item
  }
  if (url.match(/\/schedules\/.+/) && method === 'DELETE') {
    const idx = mockSchedules.findIndex((s) => url.endsWith(`/${s.id}`))
    if (idx >= 0) mockSchedules.splice(idx, 1)
    return undefined
  }

  // 默认返回空
  console.warn(`[Mock] 未匹配的 API: ${method} ${url}`)
  return null
}
